/**
 * Select - Styled dropdown for form selection
 *
 * Usage:
 * <Select
 *   label="Room"
 *   value={room}
 *   onChange={setRoom}
 *   options={[
 *     { value: 'living', label: 'Living Room' },
 *     { value: 'bedroom', label: 'Bedroom' },
 *   ]}
 * />
 *
 * // Without label
 * <Select
 *   label="Sort by"
 *   hideLabel
 *   value={sort}
 *   onChange={setSort}
 *   options={sortOptions}
 * />
 */

import { ChevronDown } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Dropdown, type DropdownItem } from '../overlays';
import { cn } from '../utils';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

export interface SelectProps {
  /** Label for the select */
  label: string;
  /** Hide the label visually (still accessible to screen readers) */
  hideLabel?: boolean;
  /** Placeholder text when no value selected */
  placeholder?: string;
  /** Currently selected value */
  value: string;
  /** Called when selection changes */
  onChange: (value: string) => void;
  /** Available options */
  options: SelectOption[];
  /** Error message */
  error?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Additional classes for the container */
  className?: string;
  /** Full width */
  fullWidth?: boolean;
}

export function Select({
  label,
  hideLabel,
  placeholder = 'Select...',
  value,
  onChange,
  options,
  error,
  disabled,
  className,
  fullWidth,
}: SelectProps) {
  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label ?? placeholder;
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [minWidth, setMinWidth] = useState<number | null>(null);

  const measureEntries = useMemo(() => {
    const entries: Array<{ text: string; className: string }> = [];
    options.forEach((option) => {
      if (option.label) {
        entries.push({ text: option.label, className: 'text-sm font-normal' });
      }
      if (option.description) {
        entries.push({ text: option.description, className: 'text-xs font-normal' });
      }
    });
    if (placeholder) {
      entries.push({ text: placeholder, className: 'text-sm font-normal' });
    }
    return entries;
  }, [options, placeholder]);

  useLayoutEffect(() => {
    if (!measureRef.current) return;
    const nodes = Array.from(measureRef.current.querySelectorAll<HTMLElement>('[data-measure]'));
    if (nodes.length === 0) return;

    let maxWidth = 0;
    nodes.forEach((node) => {
      maxWidth = Math.max(maxWidth, node.scrollWidth);
    });

    const hasIcons = options.some((option) => option.icon);
    const paddingAndChevron = 48;
    const iconAllowance = hasIcons ? 20 : 0;
    setMinWidth(Math.ceil(maxWidth + paddingAndChevron + iconAllowance));
  }, [measureEntries, options]);

  const items: DropdownItem[] = options.map((opt) => ({
    value: opt.value,
    label: opt.label,
    description: opt.description,
    icon: opt.icon,
  }));

  return (
    <div
      className={cn('relative', fullWidth && 'w-full', className)}
      style={minWidth ? { minWidth } : undefined}
    >
      <label
        className={cn(
          'mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300',
          hideLabel && 'sr-only'
        )}
      >
        {label}
      </label>
      <Dropdown
        trigger={({ isOpen }) => (
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors',
              'border-gray-300 bg-gray-50 hover:border-gray-400',
              'dark:border-white/[0.06] dark:bg-[#18181b] dark:hover:border-white/[0.1]',
              selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-zinc-500',
              error && 'border-red-500 dark:border-red-500',
              disabled && 'cursor-not-allowed opacity-50'
            )}
            style={minWidth ? { minWidth } : undefined}
          >
            <span className="truncate">{displayLabel}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 flex-shrink-0 text-gray-500 transition-transform dark:text-zinc-500',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        )}
        items={items}
        value={value}
        onSelect={onChange}
      />
      {error && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none absolute -z-10 whitespace-nowrap opacity-0"
      >
        {measureEntries.map((entry, index) => (
          <span key={`${entry.text}-${index}`} data-measure className={entry.className}>
            {entry.text}
          </span>
        ))}
      </div>
    </div>
  );
}
